import { useEffect, useState, useRef } from "react";
import { getElementByKey } from "../engines/renderingEngine/renderer";
import {
  getViewport,
  isViewportInitialized
} from "../pixi-components/layers/viewport/viewport.layer";

const REAPPEAR_DELAY = 100;

/**
 * This function will return the global coordinates of the component.
 * The componentKey is with respect to the core component instance.
 *
 * Global coordinations are actual screen coordinates. Local coordinates
 * are the coordinates with respect to the viewport. So Global coordinates
 * gets calculated by considering the viewport position and zoom level.
 *
 * @param componentKey
 * @returns
 */
export const getComponentLocation = (componentKey: string | undefined) => {
  const viewport = getViewport();
  // If component key is not set, it means that no component is selected.
  if (!componentKey) {
    return null;
  }

  // Get the component instance
  const componentInstance = getElementByKey(componentKey);

  if (componentInstance) {
    // Convert coordinates to local coordinates
    const {
      x,
      y,
      width: componentWidth,
      height: componentHeight
    } = componentInstance.getLayoutValues();
    const localPoint = viewport.toGlobal({
      x,
      y
    });

    // Get zoom level
    const zoom = viewport.scale.x;

    // Calculate the width & height of the component using the zoom level
    const width = componentWidth * zoom;
    const height = componentHeight * zoom;

    // Update the local point state
    return { ...localPoint, width, height };
  } else {
    // If component instance is not found, we unset the local point
    return null;
  }
};

/**
 * When viewport changes zoom or position, this hook will evaluate the position
 * of the component and return the global coordinates of the component. Global
 * coordinate are the coordinates with respect to the browser window.
 */
export const useComponentLocation = (componentKey: string | undefined) => {
  const [localPoint, setLocalPoint] = useState({
    x: NaN,
    y: NaN,
    width: NaN,
    height: NaN
  });
  const movingRef = useRef<boolean>(false);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    /**
     * This is the event handler for the viewport. It will be called when the
     * viewport is moved or zoomed. It will update the local point state.
     *
     * But it will not do it immediately to avoid unnecessary re-renders. It
     * will wait for 200ms and then update the state after the last event gets
     * triggered. And leading event will set the localPoint to NaN.
     *
     * The perceived behavior is as follows:
     * - User starts dragging -> Returned coordinates are NaN
     * - User stops dragging (for 100ms) -> Nothing happens
     * - User continues dragging/zooming -> Nothing happens
     * - User stops dragging/zooming -> Returned coordinates are updated
     */

    const updateLocalPoint = () => {
      const localPoint = getComponentLocation(componentKey);
      if (localPoint) {
        setLocalPoint(localPoint);
      } else {
        setLocalPoint({ x: NaN, y: NaN, width: NaN, height: NaN });
      }
      updateTimeoutRef.current = null;
      movingRef.current = false;
    };

    const handleInteraction = () => {
      if (!movingRef.current) {
        // If no pending update is scheduled, we unset the local point
        // because this is the first event of the series.
        setLocalPoint({ x: NaN, y: NaN, width: NaN, height: NaN });
      }

      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      movingRef.current = true;

      updateTimeoutRef.current = setTimeout(updateLocalPoint, REAPPEAR_DELAY);
    };

    // This is for the case where the component is selected and the user tries to navigate to app-details page in which case the viewport is not initialized but the component is still selected
    if (!componentKey || !isViewportInitialized()) {
      return;
    }
    const viewport = getViewport();

    if (viewport) {
      // Initial emit
      handleInteraction();

      // Add the event listener
      viewport.on("moved", handleInteraction);
      viewport.on("zoomed", handleInteraction);
      return () => {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        // TODO: To check if the code below runs , since it will not as the viewport de-initialization will happen first before this gets executed
        if (!isViewportInitialized()) return;

        const viewport = getViewport();
        // Remove the event listener
        viewport.off("moved", handleInteraction);
        viewport.off("zoomed", handleInteraction);
      };
    }
  }, [componentKey]);

  return localPoint;
};
